import React, { useState } from 'react';
import { injectIntl } from 'react-intl';

import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';

import {
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Typography,
} from '@material-ui/core';

import ChartCard from '~/components/ChartCard';
import LocaleMessage from '~/components/LocaleMessage';

import { ChatCardContainer } from '../../Dashboard/styles';

const DEFAULT_METRICS = [
    {
        value: 'tasks',
        labelId: 'page.robots.cleaning_report.group_comparison.metrics.tasks',
        unit: '',
    },
    {
        value: 'time',
        labelId: 'page.robots.cleaning_report.group_comparison.metrics.time',
        unit: 'h',
    },
    {
        value: 'area',
        labelId: 'page.robots.cleaning_report.group_comparison.metrics.area',
        unit: 'm²',
    },
    {
        value: 'frequency',
        labelId:
            'page.robots.cleaning_report.group_comparison.metrics.frequency',
        unit: '',
    },
];

/**
 * Componente que renderiza a visualização comparativa de performance de limpeza entre os diferentes grupos de um SAAS.
 *
 * Este card só deve estar visível para contas de Administradores (master/admin), pois exibe
 * somatórias de dados reais agregadas do histórico de tarefas da Pluginspace.
 *
 * @param {Object} props Propriedades do componente.
 * @param {Array<Object>} props.data Array contendo objetos com `id`, `name`, `tasks`, `time`, `area` e `frequency`.
 * @param {string} props.cardColor Cor principal utilizada para preencher os gráficos da dashboard.
 *
 * @example
 * // Exemplo de uso
 * <GroupComparisonDashboardCard
 *    data={[{ id: 1, name: 'Grupo Sul', tasks: 15, area: 4500, time: 2.5, frequency: 10 }]}
 *    cardColor="#1A2B3C"
 * />
 */
function GroupComparisonDashboardCard({
    data = [],
    cardColor = '#8884d8',
    intl,
    extraMetrics = [],
}) {
    const METRICS = [...DEFAULT_METRICS, ...extraMetrics];
    const [selectedMetric, setSelectedMetric] = useState('tasks');

    const activeMetric = METRICS.find(m => m.value === selectedMetric);

    const renderChart = () => {
        if (!data || data.length === 0) {
            return (
                <Typography>
                    <LocaleMessage msg="page.robots.cleaning_report.group_comparison.no_data" />
                </Typography>
            );
        }

        return (
            <ResponsiveContainer width="100%" height={300}>
                <BarChart
                    data={data}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis />
                    <Tooltip
                        formatter={value =>
                            value != null ? Number(value).toFixed(2) : value
                        }
                    />
                    <Legend />
                    <Bar
                        dataKey={selectedMetric}
                        name={intl.formatMessage({
                            id: activeMetric.labelId,
                        })}
                        fill={cardColor}
                        radius={[4, 4, 0, 0]}
                    />
                </BarChart>
            </ResponsiveContainer>
        );
    };

    return (
        <div className="row card-row">
            <div className="col-12 dash-card mb-5">
                <ChatCardContainer>
                    <ChartCard
                        className="animated slideInFromLeft animation-duration-3"
                        title={intl.formatMessage({
                            id:
                                'page.robots.cleaning_report.group_comparison.title',
                        })}
                        desc={intl.formatMessage({
                            id:
                                'page.robots.cleaning_report.group_comparison.desc',
                        })}
                        value=""
                    >
                        <div className="row">
                            <div className="col-12 mb-3">
                                <div
                                    className="d-flex align-items-center justify-content-end mb-3"
                                    style={{ gap: '15px' }}
                                >
                                    <FormControl
                                        size="small"
                                        style={{ minWidth: 200 }}
                                    >
                                        <InputLabel>
                                            <LocaleMessage msg="page.robots.cleaning_report.group_comparison.metric_label" />
                                        </InputLabel>
                                        <Select
                                            value={selectedMetric}
                                            onChange={e =>
                                                setSelectedMetric(
                                                    e.target.value
                                                )
                                            }
                                        >
                                            {METRICS.map(m => (
                                                <MenuItem
                                                    key={m.value}
                                                    value={m.value}
                                                >
                                                    {intl.formatMessage({
                                                        id: m.labelId,
                                                    })}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </div>
                            </div>
                            <div className="col-12 area-chart-container">
                                {renderChart()}
                            </div>
                        </div>
                    </ChartCard>
                </ChatCardContainer>
            </div>
        </div>
    );
}

/**
 * NOTA DE ARQUITETURA (i18n):
 * O componente é exportado usando o HOC `injectIntl` devido à limitação da versão
 * 2.9.0 do `react-intl`, que ainda não suportava o hook moderno `useIntl()`.
 * Essa foi a forma necessária e arquiteturalmente correta de injetar a _prop_ `intl` para traduzir
 * os eixos e opções de gráficos dinamicamente no JavaScript.
 */
export default injectIntl(GroupComparisonDashboardCard);
